<script lang="ts" setup>
import { computed, onBeforeMount, provide, ref, watch } from 'vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import { useRoute, useRouter } from 'vue-router/composables'
import {
  TCreateEventFormComponent,
  TCreateEventFormKey,
  CREATE_EVENT_FORMS,
} from '@/pages/CreateEventProcessingPage/static'
import { useEventEditQuery } from '@/enitites/event/api/queries'
import EventEditLayout from '@/pages/CreateEventProcessingPage/ui/layout/EventEditLayout.vue'

const router = useRouter()
const route = useRoute()

const screenOrder = ref<TCreateEventFormKey[]>(['info', 'date', 'battles', 'display-mode', 'payment-mode', 'total'])
const currentEventStep = ref<number>(0)
const totalEventSteps = computed(() => screenOrder.value.length)
const currentScreen = computed<TCreateEventFormComponent>(() => {
  return CREATE_EVENT_FORMS[screenOrder.value[currentEventStep.value]]
})

provide('currentEventStep', currentEventStep)
provide('totalEventSteps', totalEventSteps)
provide('isCreationFlow', true)

const isClickBtnNext = ref(false)

const { data: eventEdit, error, isLoading } = useEventEditQuery(Number(route.params.id))

const getUserDB = computed(() => store.getters.getUserDB)

const handleNext = async () => {
  if (currentEventStep.value < totalEventSteps.value - 1) {
    isClickBtnNext.value = true
    currentEventStep.value += 1
  } else {
    await router.push({ name: 'Event', params: { id: String(route.params.id) } })
    store.dispatch('showAlertToast', 'eventCreated')
    store.dispatch('setShowEventInfoModal', true)
  }
}

const handleBack = () => {
  if (currentEventStep.value > 0) {
    isClickBtnNext.value = false
    currentEventStep.value -= 1
  }
}

const handleCancel = () => {
  store.dispatch('showAlertToast', 'eventSavedAsDraft')
  router.push({ name: 'Event', params: { id: String(route.params.id) } })
}

onBeforeMount(() => {
  if (!getUserDB.value) {
    router.push({ name: 'EventsGrid' })
  }
})

watch(error, () => {
  if (error.value) {
    store.dispatch('showAlertToast', {
      type: 'error',
      message: error.value.message,
    })
  }
  router.push({ name: 'Event', params: { id: String(route.params.id) } })
})

watch(
  () => eventEdit.value?.paymentSystem?.isEnabled,
  (newVal) => {
    if (newVal === false) {
      screenOrder.value = ['info', 'date', 'battles', 'display-mode', 'payment-mode', 'own-payment-mode', 'total']
    } else if (newVal === true) {
      screenOrder.value = [
        'info',
        'date',
        'battles',
        'display-mode',
        'payment-mode',
        'bank-account',
        'tickets',
        'refund-policy',
        'guests',
        'total',
      ]
    }
  },
  { immediate: true }
)
</script>

<template>
  <EventEditLayout
    :title="currentScreen.pageTitle"
    :description="currentScreen.pageDescription"
    :loading="isLoading"
    :error="error?.message"
  >
    <transition
      :name="isClickBtnNext ? 'slide-fade' : 'slide-fade-revers'"
      appear
      mode="out-in"
    >
      <component
        :is="currentScreen.form"
        v-if="eventEdit"
        :component="currentScreen.form"
        :event-data="eventEdit"
        @next="handleNext"
        @back="handleBack"
        @cancel="handleCancel"
      />
    </transition>
  </EventEditLayout>
</template>

<style lang="scss" scoped>
@import '@/assets/style/slideFade';
</style>
